import { menuRoute } from "@/App";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/custom/Drawer";
import { useLocationMenus } from "@/hooks/useLocationMenus";
import { cn } from "@/lib/utils";
import { CircleDotIcon, CircleIcon, MenuIcon } from "lucide-react";
import { useMemo, useState } from "react";

export function MenuSelectorDialog({ onClose }: { onClose?: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const { menuId } = menuRoute.useParams();

  const { hasMultipleMenus, availableMenus, changeMenu } = useLocationMenus();

  const selectedMenu = useMemo(() => {
    return availableMenus.find((menu) => menu.id === menuId);
  }, [availableMenus, menuId]);

  if (!hasMultipleMenus) return null;
  if (availableMenus.length === 0) return null;
  if (availableMenus.length === 1) return null;

  return (
    <>
      <Button
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(true);
        }}
        size="icon"
        className="h-12 w-12 rounded-lg bg-background hover:bg-background/90 text-foreground shadow-lg transition-all duration-200 hover:scale-110"
      >
        <div className="size-6 flex items-center justify-center rounded-full aspect-square text-xsfont-medium">
          <MenuIcon strokeWidth={1.5} size={16} />
        </div>
      </Button>

      <span
        className="absolute top-1/2 left-0 translate-x-14 text-left -translate-y-1/2 text-sm  whitespace-nowrap bg-background py-1 px-3 rounded-lg text-foreground"
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(true);
        }}
      >
        Change menu
      </span>

      <Drawer
        isOpen={isOpen}
        closeDrawer={() => {
          setIsOpen(false);
          onClose?.();
        }}
        classes={{
          paper:
            "rounded-t-xl md:rounded-tr-none md:rounded-bl-xl !overflow-visible bg-background ",
        }}
      >
        <div className="flex flex-col overflow-hidden text-foreground md:h-screen">
          <header className="flex flex-col gap-2 px-4 pb-4 pt-8">
            <span className="text-xl font-medium tracking-tighter">
              Change menu
            </span>
            <div className="text-muted-foreground max-w-prose text-left">
              Select a menu to view - you can pick items from multiple menus in
              the same order.
            </div>
          </header>

          <div className="grid gap-2 max-md:max-h-[40vh] md:max-h-[80vh] overflow-y-auto p-4">
            {availableMenus.map((menu) => {
              const isSelected = selectedMenu?.id === menu.id;
              return (
                <Button
                  key={menu.id}
                  onClick={() => {
                    changeMenu(menu.id);
                    setIsOpen(false);
                  }}
                  variant={isSelected ? "default" : "outline"}
                  className={cn("flex gap-3 justify-start h-12")}
                >
                  {isSelected ? (
                    <CircleDotIcon
                      className="text-primary-foreground"
                      strokeWidth={1.5}
                      size={20}
                    />
                  ) : (
                    <CircleIcon
                      className="text-foreground"
                      strokeWidth={1.5}
                      size={20}
                    />
                  )}
                  <span className="text-sm">{menu.name}</span>
                </Button>
              );
            })}
          </div>

          <footer className="py-2 bg-white border-t mt-auto">
            <Button
              onClick={() => setIsOpen(false)}
              variant={"link"}
              className="w-full"
            >
              Cancel
            </Button>
          </footer>
        </div>
      </Drawer>
    </>
  );
}
